import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, Command, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { api, formatApiError } from '@/lib/api';

const DEBOUNCE_MS = 250;

/**
 * Sesión H — RBAC-aware global search bar.
 * Hits GET /api/admin/search. Server enforces scoping per role; we just
 * render the response. `evaluated` role NEVER sees this component
 * (Topbar gates it).
 */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Run the actual search.
  const search = useCallback(async (query) => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get('/admin/search', { params: { q: query } });
      setGroups(data?.groups || []);
    } catch (e) {
      setError(formatApiError(e));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce on every keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setGroups([]); setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => search(q.trim()), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [q, search]);

  // Click-outside to close dropdown.
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const showDropdown = open && (q.trim().length >= 2 || loading || error);

  const handleSelect = (link) => {
    setOpen(false);
    setQ('');
    if (link) navigate(link);
  };

  return (
    <div
      ref={wrapRef}
      className="hidden md:flex items-center relative flex-1 max-w-72 min-w-44 shrink"
      data-testid="global-search"
    >
      <Search className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input
        data-testid="global-search-input"
        placeholder={t('topbar.search')}
        className="pl-9 pr-14 h-9 bg-muted/50 border-border/60 focus-visible:ring-primary/30"
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
      />
      <kbd className="absolute right-2 inline-flex items-center gap-0.5 rounded border border-border bg-background px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
        <Command className="h-3 w-3" /> K
      </kbd>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 top-full mt-1 max-h-[420px] overflow-y-auto rounded-md border border-border bg-popover shadow-lg z-30"
          data-testid="global-search-dropdown"
        >
          {loading && (
            <div className="px-3 py-3 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t('topbar.searchLoading')}
            </div>
          )}
          {!loading && error && (
            <div className="px-3 py-3 text-xs text-destructive">{error}</div>
          )}
          {!loading && !error && groups.length === 0 && q.trim().length >= 2 && (
            <div className="px-3 py-3 text-xs text-muted-foreground" data-testid="global-search-empty">
              {t('topbar.searchEmpty')}
            </div>
          )}
          {!loading && !error && q.trim().length < 2 && (
            <div className="px-3 py-3 text-xs text-muted-foreground">
              {t('topbar.searchHint')}
            </div>
          )}
          {!loading && groups.map((g) => (
            <div key={g.type} className="border-b border-border/50 last:border-b-0" data-testid={`search-group-${g.type}`}>
              <div className="px-3 pt-2 pb-1 text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                {t(`topbar.searchGroups.${g.type}`, { defaultValue: g.label })}
              </div>
              <ul>
                {g.items.map((it) => (
                  <li key={it.id}>
                    <button
                      type="button"
                      data-testid={`search-item-${it.type}-${it.id}`}
                      onClick={() => handleSelect(it.link)}
                      className="w-full text-left px-3 py-2 hover:bg-muted/60 flex flex-col gap-0.5"
                    >
                      <span className="text-sm font-medium truncate">{it.label}</span>
                      {it.sublabel && (
                        <span className="text-xs text-muted-foreground truncate">{it.sublabel}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {g.has_more && (
                <button
                  type="button"
                  data-testid={`search-see-all-${g.type}`}
                  onClick={() => handleSelect(g.items[0]?.link?.replace(/\/[^/]+$/, ''))}
                  className="block w-full text-left px-3 py-1.5 text-xs text-primary hover:bg-primary/5"
                >
                  {t('topbar.searchSeeAll')} →
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
